import React, { useState } from 'react';

import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { Box, Container, useMediaQuery } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { isLTLEHotelOfferType } from '~/utils/offer';

import { type OfferBuilderProperty, formatPropertyLocation } from './Content/PropertyInformation';
import OfferBuilderSidebar from './OfferBuilderSidebar';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'offerBuilderSidebarCollapsed';
const SIDEBAR_AUTO_COLLAPSE_QUERY = '(max-width:1440px)';

const WORKFLOW_ITEM_BY_PATH: Record<string, string> = {
  assets: 'Assets',
  'cm-checklist': 'CM Checklist',
  content: 'Content',
  'deal-information': 'Deal Information',
  'hotel-proposal-pricing-tool': 'Hotel Proposal Pricing Tool',
  'rates-and-inventory': 'Rates and Inventory',
};

export default function OfferBuilderShell() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { pathname } = useLocation();
  const { data: offer, isFetched: isOfferFetched } = useGetOffer(offerId);
  const [manualSidebarCollapsed, setManualSidebarCollapsed] = useState<boolean | null>(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    return stored === null ? null : stored === 'true';
  });
  const prefersCollapsedByBreakpoint = useMediaQuery(SIDEBAR_AUTO_COLLAPSE_QUERY);
  const isSidebarCollapsed = manualSidebarCollapsed ?? prefersCollapsedByBreakpoint;
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const propertyId = isSupportedOfferType
    ? offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;
  const { data: property } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });
  const activePath = pathname.split('/').filter(Boolean).pop() ?? 'content';
  const activeItem = WORKFLOW_ITEM_BY_PATH[activePath] ?? 'Content';
  const hotelName = property?.name || propertyId || 'Property';
  const propertyLocation = formatPropertyLocation(property);

  const toggleSidebarCollapsed = () => {
    setManualSidebarCollapsed((current) => {
      const next = !(current ?? prefersCollapsedByBreakpoint);
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  };

  // Checks for a package actually linked to a property; redirect to create-a-Flash wizard
  // to complete setup if none found.
  if (isOfferFetched && isSupportedOfferType && offerId && offer?.vendor_account_id && !propertyId) {
    return <Navigate to={`/vendors/${offer.vendor_account_id}?setupFlashOffer=${offerId}`} replace />;
  }

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: isSidebarCollapsed ? '56px minmax(0, 1fr)' : '280px minmax(0, 1fr)' },
        }}
      >
        <OfferBuilderSidebar
          activeItem={activeItem}
          collapsed={isSidebarCollapsed}
          hotelCode={property?.hotel_code}
          hotelName={hotelName}
          location={propertyLocation}
          offerId={offerId}
          onToggleCollapsed={toggleSidebarCollapsed}
        />

        <Box
          sx={{
            '--offer-builder-action-left': isSidebarCollapsed ? '112px' : '336px',
            minWidth: 0,
            px: { xs: 2, md: 4 },
            py: 3,
            pb: 14,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Container>
  );
}
